import {
  type AreaModel,
  type BuyerModel,
  type CategoryModel,
  type DietModel,
  type FarmModel,
  type FarmKeeperModel,
  type ItemModel,
  type LotModel,
  type SupplierModel,
} from '@@types/models';
import { type GainCurveModel } from '@@types/models/gainCurve-model';
import { type ProgramModel } from '@@types/models/program-model';
import { api } from '@services/api';
import { queryKeys } from '@services/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@services/toast';
import { AuthContext } from './AuthContext';

type FarmProviderProps = {
  children: ReactNode;
};

type FarmInfo = {
  scores?: any[];
  scoreMeasurement?: 'percentage';
  keepers?: FarmKeeperModel[];
};

type LicenseMode = 'CONFINEMENT_ONLY' | 'PASTURE_ONLY' | 'BOTH';
type LicenseFeature = 'automation';

type LicenseEntitlement = {
  mode: LicenseMode;
  features: LicenseFeature[];
};

type FarmContextData = {
  farm: string | undefined;
  farms: FarmModel[] | undefined;
  farmsLoading: boolean;
  farmsError: boolean;
  financialScope: string | undefined;
  canSelectAllFarms: boolean;
  selectionVersion: number;
  selectFinancialScope: (scope: string) => void;
  /** Apenas a sincronização de navegação, depois de validar/bloquear a transição. */
  applyFinancialScope: (scope: string) => void;
  leaveAllFarms: () => void;
  registerBeforeFarmChange: (guard: () => Promise<boolean>) => () => void;
  categories?: CategoryModel[] | undefined;
  diets?: DietModel[] | undefined;
  areas?: AreaModel[] | undefined;
  items?: ItemModel[] | undefined;
  treatments?: string[] | undefined;
  scores?: any[] | undefined;
  farmInfo?: FarmInfo | undefined;
  programs?: ProgramModel[] | undefined;
  lots?: LotModel[] | undefined;
  gainCurves?: GainCurveModel[] | undefined;
  suppliers?: SupplierModel[] | undefined;
  /** Compradores que podem ser escolhidos agora (ativos e visíveis na fazenda). */
  buyers?: BuyerModel[] | undefined;
  /** Todos os compradores da fazenda, inclusive inativos/ocultos: só para mostrar o nome em operações passadas. */
  allBuyers?: BuyerModel[] | undefined;
  licenseMode?: LicenseMode;
  licenseFeatures?: LicenseFeature[];
  update?: boolean;
  selectedDiet?: DietModel | undefined;
  setSelectedDiet?: (diet: DietModel | undefined) => void;
  selectFarm: (id: string) => void;
};

export const FarmContext = createContext({} as FarmContextData);

export const ALL_FARMS = 'todas';
const FARM_STORAGE_KEY = '@groud:selected-farm';

export function FarmProvider({ children }: FarmProviderProps) {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [update, setUpdate] = useState<boolean>(false);
  const [storedFarm, setFarm] = useState<string | undefined>(() => {
    const storedFarm = localStorage.getItem(FARM_STORAGE_KEY);
    return storedFarm || undefined;
  });
  const [allFarms, setAllFarms] = useState(false);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const beforeFarmChange = useRef<(() => Promise<boolean>) | null>(null);
  const [selectedDiet, setSelectedDiet] = useState<DietModel | undefined>();

  async function fetchModel<T>(model: string, farmId: string | undefined): Promise<T | null> {
    if (!model || !farmId) {
      return null;
    }

    const endpoints: Record<string, string> = {
      diet: `/farms/${farmId}/diets/all`,
      area: `/farms/${farmId}/areas/all`,
      lot: `/farms/${farmId}/lots/all`,
      gainCurve: `/farms/${farmId}/gain-curves/all`,
      program: `/farms/${farmId}/programs/all`,
      item: `/farms/${farmId}/items/all`,
      supplier: `/farms/${farmId}/suppliers/all?include=all`,
      buyer: `/farms/${farmId}/buyers/all?include=all`,
    };
    const endpoint = endpoints[model];
    if (!endpoint) {
      throw new Error(`Unsupported farm model "${model}"`);
    }
    const result = await api.get(endpoint);
    return result.data;
  }

  const onError = (error: any) => {
    // 403 aqui significa usuário sem permissão/licença para um dos modelos
    // auxiliares (dietas, áreas, programas...). É condição esperada para
    // usuários restritos — o backend continua sendo a fonte de verdade — e
    // não deve virar toast de erro em toda carga do app.
    if (error?.response?.status === 403) {
      return;
    }
    toast.errorFrom(error, 'Não foi possível.', undefined, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      type: 'error',
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
    });
  };

  const farmsQuery = useQuery<FarmModel[]>({
    queryKey: queryKeys.farms.all(user?.id),
    queryFn: async () => {
      const result = await api.get('/farms');
      return result.data?.docs;
    },
    enabled: isAuthenticated,
  });

  // Nunca expor um ID persistido antes de conferir o acesso atual.
  const farm = farmsQuery.data?.some((entry) => entry.id === storedFarm) ? storedFarm : undefined;
  const canSelectAllFarms = Boolean(isAuthenticated && user?.view?.includes('financialManagement'));
  const financialScope = allFarms && canSelectAllFarms && farmsQuery.data?.length ? ALL_FARMS : farm;
  const isValidScope = useCallback((scope: string) => {
    return scope === ALL_FARMS
      ? canSelectAllFarms && Boolean(farmsQuery.data?.length)
      : Boolean(farmsQuery.data?.some((entry) => entry.id === scope));
  }, [canSelectAllFarms, farmsQuery.data]);
  const validateCurrentScope = useRef(isValidScope);
  useLayoutEffect(() => { validateCurrentScope.current = isValidScope; }, [isValidScope]);

  const applyFinancialScope = useCallback((scope: string) => {
    if (!isValidScope(scope)) return;
    setAllFarms(scope === ALL_FARMS);
    if (scope !== ALL_FARMS) {
      setFarm(scope);
      if (scope !== farm) setSelectedDiet(undefined);
      localStorage.setItem(FARM_STORAGE_KEY, scope);
    }
  }, [farm, isValidScope]);

  const selectFinancialScope = useCallback((scope: string) => {
    if (!isValidScope(scope)) return;
    void (async () => {
      if (scope !== financialScope && beforeFarmChange.current && !(await beforeFarmChange.current())) return;
      // O acesso pode mudar enquanto a confirmação está aberta.
      if (!validateCurrentScope.current(scope)) return;
      applyFinancialScope(scope);
      setSelectionVersion((version) => version + 1);
    })();
  }, [applyFinancialScope, financialScope, isValidScope]);

  const selectFarm = useCallback((id: string) => {
    if (id !== ALL_FARMS) selectFinancialScope(id);
  }, [selectFinancialScope]);

  const leaveAllFarms = useCallback(() => { setAllFarms(false); }, []);
  const registerBeforeFarmChange = useCallback((guard: () => Promise<boolean>) => {
    beforeFarmChange.current = guard;
    return () => { if (beforeFarmChange.current === guard) beforeFarmChange.current = null; };
  }, []);

  // O endpoint devolve `mode` (legado) e `features` (aditivo). Uma query só
  // porque é uma leitura só — separar criaria duas chamadas para a mesma linha.
  const licenseModeQuery = useQuery<LicenseEntitlement>({
    queryKey: queryKeys.licenseMode(farm),
    queryFn: async () => {
      const result = await api.get(`/farms/${farm}/license-mode`);
      return {
        mode: (result.data?.mode as LicenseMode) || 'BOTH',
        features: Array.isArray(result.data?.features)
          ? (result.data.features as LicenseFeature[])
          : [],
      };
    },
    enabled: !!farm,
  });

  const farmInfoQuery = useQuery<FarmInfo | null>({
    queryKey: queryKeys.farms.single(farm),
    queryFn: async () => {
      const result = await api.get(`/farms/${farm}`);
      return result.data;
    },
    enabled: !!farm,
  });

  const dietsQuery = useQuery<DietModel[] | null>({
    queryKey: queryKeys.diets.all(farm),
    queryFn: async () => await fetchModel<DietModel[]>('diet', farm),
    enabled: !!farm,
  });

  const areasQuery = useQuery<AreaModel[] | null>({
    queryKey: queryKeys.areas.all(farm),
    queryFn: async () => await fetchModel<AreaModel[]>('area', farm),
    enabled: !!farm,
  });

  const lotsQuery = useQuery<LotModel[] | null>({
    queryKey: queryKeys.lots.all(farm),
    queryFn: async () => await fetchModel<LotModel[]>('lot', farm),
    enabled: !!farm,
  });

  const gainCurvesQuery = useQuery<GainCurveModel[] | null>({
    queryKey: ['gainCurves', farm],
    queryFn: async () => await fetchModel<GainCurveModel[]>('gainCurve', farm),
    enabled: !!farm,
  });

  const programsQuery = useQuery<ProgramModel[] | null>({
    queryKey: queryKeys.programs.all(farm),
    queryFn: async () => await fetchModel<ProgramModel[]>('program', farm),
    enabled: !!farm,
  });

  const itemsQuery = useQuery<ItemModel[] | null>({
    queryKey: queryKeys.items.all(farm),
    queryFn: async () => await fetchModel<ItemModel[]>('item', farm),
    enabled: !!farm,
  });

  const suppliersQuery = useQuery<SupplierModel[] | null>({
    queryKey: queryKeys.suppliers.all(farm),
    queryFn: async () => await fetchModel<SupplierModel[]>('supplier', farm),
    enabled: !!farm,
  });

  const buyersQuery = useQuery<BuyerModel[] | null>({
    queryKey: queryKeys.buyers.all(farm),
    queryFn: async () => await fetchModel<BuyerModel[]>('buyer', farm),
    enabled: !!farm,
  });

  const modelQueries = useMemo(
    () => [farmsQuery, licenseModeQuery, farmInfoQuery, dietsQuery, areasQuery, lotsQuery, gainCurvesQuery, programsQuery, itemsQuery, suppliersQuery, buyersQuery],
    [farmsQuery, licenseModeQuery, farmInfoQuery, dietsQuery, areasQuery, lotsQuery, gainCurvesQuery, programsQuery, itemsQuery, suppliersQuery, buyersQuery]
  );

  useEffect(() => {
    if (farmsQuery.data && storedFarm) {
      const farmExists = farmsQuery.data.some((farmItem) => farmItem.id === storedFarm);
      if (!farmExists) {
        // Um link válido pode ter escolhido outra fazenda no mesmo commit.
        setFarm((current) => current === storedFarm ? undefined : current);
        setSelectedDiet(undefined);
        if (localStorage.getItem(FARM_STORAGE_KEY) === storedFarm) localStorage.removeItem(FARM_STORAGE_KEY);
      }
    }
  }, [storedFarm, farmsQuery.data]);

  useEffect(() => {
    if (farmsQuery.data) {
      setUpdate((prev) => !prev);
    }
  }, [farmsQuery.data]);

  useEffect(() => {
    modelQueries.forEach((query) => {
      if (query.error) {
        onError(query.error);
      }
    });
  }, [modelQueries]);

  return (
    <FarmContext.Provider
      value={{
        farm,
        farms: farmsQuery.data,
        farmsLoading: farmsQuery.isPending,
        farmsError: farmsQuery.isError,
        financialScope,
        canSelectAllFarms,
        selectionVersion,
        selectFinancialScope,
        applyFinancialScope,
        leaveAllFarms,
        registerBeforeFarmChange,
        update,
        selectFarm,
        farmInfo: farmInfoQuery.data ?? undefined,
        diets: dietsQuery.data ?? undefined,
        areas: areasQuery.data ?? undefined,
        lots: lotsQuery.data ?? undefined,
        gainCurves: gainCurvesQuery.data ?? undefined,
        programs: programsQuery.data ?? undefined,
        items: itemsQuery.data ?? undefined,
        selectedDiet,
        suppliers: suppliersQuery.data ?? undefined,
        buyers: buyersQuery.data?.filter((buyer) => buyer.selectable !== false),
        allBuyers: buyersQuery.data ?? undefined,
        licenseMode: licenseModeQuery.data?.mode,
        licenseFeatures: licenseModeQuery.data?.features,
        setSelectedDiet,
      }}
    >
      {children}
    </FarmContext.Provider>
  );
}
